import React, { useEffect, useState, useRef } from 'react';
import './PostLoginHome.css';
import ChatAssistant from '../components/chat-assistant/ChatAssistant';
import VideoReels from './VideoReels';

const CRM_API_BASE = process.env.REACT_APP_CRM_API_URL || 'http://localhost:6001/api';

// small helper to perform fetch with an AbortController timeout so the UI doesn't hang
async function fetchJson(url, opts = {}, timeoutMs = 12000) {
  const controller = new AbortController();
  const sig = controller.signal;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, Object.assign({}, opts, { signal: sig }));
    clearTimeout(timer);
    return res;
  } catch (e) {
    clearTimeout(timer);
    throw e;
  }
}

function FriendlyDate(ts) {
  try { return new Date(ts).toLocaleString(); } catch (e) { return ts; }
}

export default function PostLoginHome() {
  // Location + UI state
  const [location, setLocation] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(() => {
    try { return !localStorage.getItem('zz_location') && !localStorage.getItem('zz_location_prompt_dismissed'); } catch (e) { return true; }
  });
  
  // View mode state
  const [viewMode, setViewMode] = useState('feed'); // 'feed' or 'reels'

  // Events state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [today, setToday] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [categoriesToday, setCategoriesToday] = useState([]);
  const [categoriesUpcoming, setCategoriesUpcoming] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const seenViewsRef = useRef(new Set());

  // News state
  const [newsSummary, setNewsSummary] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState(null);
  const [newsExpanded, setNewsExpanded] = useState(false);

  // Places state
  const [places, setPlaces] = useState([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [placesError, setPlacesError] = useState(null);
  const [placesCategory, setPlacesCategory] = useState('restaurants');
  const [placesRadius, setPlacesRadius] = useState(() => {
    try { const v = localStorage.getItem('zz_places_radius'); return v ? Number(v) : 1200; } catch (e) { return 1200; }
  });
  const [selectedPlacesCategory, setSelectedPlacesCategory] = useState(null);
  const [isPlacesCollapsing, setIsPlacesCollapsing] = useState(false);
  const placesDetailRef = useRef(null);
  // Fixed set of place category chips (clicking calls the backend)
  // Expanded to include requested categories; these map to server-side CATEGORY_MAP keys.
  const FIXED_PLACE_CATEGORIES = [
    'schools','hospitals','stores','fuel','car_wash','sport','pharmacy','restaurants','coffee','shelter','bicycle','trails','parks','playgrounds','beach','water','tourism','attraction','worship'
  ];

  const labelize = (id) => {
    if (!id) return '';
    return id.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
  };

  // Simple client-side cache using localStorage with TTL (seconds)
  const PLACES_CACHE_TTL = 60 * 30; // 30 minutes
  const cacheKeyFor = (cat, lat, lon, radius) => `zz_places_cache:${cat}:${Math.round((lat||0)*10000)}:${Math.round((lon||0)*10000)}:${radius}`;
  const readCache = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const j = JSON.parse(raw);
      if (!j.ts || !j.data) return null;
      const age = (Date.now() - j.ts) / 1000;
      if (age > PLACES_CACHE_TTL) return null;
      return j.data;
    } catch (e) { return null; }
  };
  const writeCache = (key, data) => {
    try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch (e) { /* ignore */ }
  };

  // Prefetch a small set of common categories on startup to make UI feel snappy
  useEffect(() => {
    if (!location) return;
    const lat = location.latitude || location.lat;
    const lon = location.longitude || location.lon || location.lng;
    if (!lat || !lon) return;
    const prefetch = ['restaurants','coffee','stores', 'hotel', 'inn'];
    prefetch.forEach(cat => {
      const key = cacheKeyFor(cat, lat, lon, placesRadius);
      const cached = readCache(key);
      if (!cached) {
        // fetch in background and cache
        (async () => {
          try {
            const url = `${CRM_API_BASE.replace(/\/+$/,'')}/places?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&category=${encodeURIComponent(cat)}&radius=${encodeURIComponent(placesRadius)}&limit=12`;
            const res = await fetchJson(url, { credentials: 'include', headers: { Accept: 'application/json' } }, 12000);
            if (!res.ok) return;
            const j = await res.json(); const list = Array.isArray(j?.data) ? j.data : (Array.isArray(j) ? j : []);
            if (list && list.length) writeCache(key, list);
          } catch (e) { /* ignore prefetch errors */ }
        })();
      }
    });
  }, [location, placesRadius]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('zz_location');
      if (raw) setLocation(JSON.parse(raw));
    } catch (e) { /* ignore */ }
  }, []);

  // If the page was opened with #places hash, scroll to the Places card after mount
  useEffect(() => {
    try {
      const h = (typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash.replace('#','') : '';
      if (h === 'places') {
        setTimeout(() => {
          const el = document.querySelector('.pl-places');
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('zz-highlight');
            setTimeout(() => el.classList.remove('zz-highlight'), 2200);
          }
        }, 140);
      }
    } catch (e) { /* ignore */ }
  }, []);

  // helper: call CRM reverse geo
  const safeFetchGeo = async (lat, lon) => {
    try {
      const url = `${CRM_API_BASE.replace(/\/+$/,'')}/geo/reverse?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lon)}`;
      const res = await fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const j = await res.json();
      return j && (j.city || j.town || j.name) ? (j.city || j.town || j.name) : null;
    } catch (e) { return null; }
  };

  // Fetch events when location is available
  useEffect(() => {
    if (!location) return;
    let cancelled = false;

    const tryExtractCity = (loc) => {
      if (!loc) return null;
      const candidates = [loc.city, loc.town, loc.name, loc.neighborhood, loc.location];
      for (const c of candidates) { if (c && typeof c === 'string' && c.trim()) return c.split(',')[0].trim(); }
      return null;
    };

    const isToday = (d) => {
      if (!d) return false;
      const t = new Date(d); const now = new Date();
      return t.getFullYear() === now.getFullYear() && t.getMonth() === now.getMonth() && t.getDate() === now.getDate();
    };

    const load = async () => {
      setLoading(true); setError(null);
      try {
        let city = tryExtractCity(location);
        if (!city && location && ((location.lat && location.lon) || (location.latitude && location.longitude))) {
          const lat = location.lat || location.latitude; const lon = location.lon || location.longitude || location.lng;
          city = await safeFetchGeo(lat, lon);
        }
        if (!city && typeof navigator !== 'undefined' && navigator.geolocation) {
          try {
            const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 10000 }));
            const lat = pos.coords.latitude; const lon = pos.coords.longitude;
            city = await safeFetchGeo(lat, lon);
          } catch (e) { /* ignore */ }
        }
        if (!city) city = tryExtractCity(location) || 'your area';

        const crmUrl = `${CRM_API_BASE.replace(/\/+$/,'')}/events?location=${encodeURIComponent(city)}&limit=60`;
        const headers = { Accept: 'application/json' };
        try { const tk = localStorage.getItem('zz_token'); if (tk) headers['Authorization'] = `Bearer ${tk}`; } catch (e) {}

  const res = await fetchJson(crmUrl, { headers, credentials: 'include' }, 12000);
        if (!res.ok) throw new Error(`events ${res.status}`);
        const j = await res.json();
        const list = Array.isArray(j?.data) ? j.data : (Array.isArray(j) ? j : []);

        const tItems = []; const uItems = []; const mapT = {}; const mapU = {};
        (list || []).forEach(ev => {
          const evDate = ev.start || ev.date || ev.timestamp || ev.createdAt;
          const cat = (ev.category || 'Other').toString();
          if (isToday(evDate)) { tItems.push(ev); (mapT[cat] = mapT[cat] || []).push(ev); }
          else { uItems.push(ev); (mapU[cat] = mapU[cat] || []).push(ev); }
        });
        Object.keys(mapU).forEach(k => mapU[k].sort((a,b) => new Date(a.start||a.date||a.timestamp||a.createdAt) - new Date(b.start||b.date||b.timestamp||b.createdAt)));

        if (!cancelled) {
          setToday(tItems); setUpcoming(uItems);
          setCategoriesToday(Object.keys(mapT).map(k => ({ category: k, count: mapT[k].length, events: mapT[k] })));
          setCategoriesUpcoming(Object.keys(mapU).map(k => ({ category: k, count: mapU[k].length, events: mapU[k] })));
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('events load failed', e && e.message);
          setError('Unable to load events right now.');
          setToday([{ id: 'm1', title: 'Community Market', date: new Date().toISOString(), category: 'community', venue: 'Town Square' }]);
          setUpcoming([{ id: 'm2', title: 'Park Concert', date: new Date(Date.now() + 48*3600*1000).toISOString(), category: 'music', venue: 'Riverside' }]);
        }
      } finally { if (!cancelled) setLoading(false); }
    };

    load();
    return () => { cancelled = true; };
  }, [location]);

  // ---- Engagement tracking helpers: views, clicks, likes ----
  const trackView = async (id) => {
    if (!id) return;
    try {
      const url = `${CRM_API_BASE.replace(/\/+$/,'')}/events/${encodeURIComponent(id)}/view`;
      await fetch(url, { method: 'POST', credentials: 'include', headers: { Accept: 'application/json' } });
    } catch (e) { /* ignore tracking errors */ }
  };

  const trackClick = async (id) => {
    if (!id) return;
    try {
      const url = `${CRM_API_BASE.replace(/\/+$/,'')}/events/${encodeURIComponent(id)}/click`;
      await fetch(url, { method: 'POST', credentials: 'include', headers: { Accept: 'application/json' } });
    } catch (e) { /* ignore tracking errors */ }
  };

  const likeEvent = async (id) => {
    if (!id) return null;
    try {
      const url = `${CRM_API_BASE.replace(/\/+$/,'')}/events/${encodeURIComponent(id)}/like`;
      const res = await fetch(url, { method: 'POST', credentials: 'include', headers: { Accept: 'application/json', 'Content-Type': 'application/json' } });
      if (!res.ok) return null;
      const j = await res.json();
      return j && typeof j.likes === 'number' ? j.likes : null;
    } catch (e) { return null; }
  };

  const unlikeEvent = async (id) => {
    if (!id) return null;
    try {
      const url = `${CRM_API_BASE.replace(/\/+$/,'')}/events/${encodeURIComponent(id)}/unlike`;
      const res = await fetch(url, { method: 'POST', credentials: 'include', headers: { Accept: 'application/json', 'Content-Type': 'application/json' } });
      if (!res.ok) return null;
      const j = await res.json();
      return j && typeof j.likes === 'number' ? j.likes : null;
    } catch (e) { return null; }
  };

  // Update event objects in all relevant UI state containers
  const updateEventLikesInState = (eventId, newLikes) => {
    if (!eventId) return;
    const updateArr = (arrSetter) => arrSetter(prev => (prev || []).map(it => {
      try { if ((it.id || it._id || it.title) && String(it.id || it._id || it.title) === String(eventId)) { return { ...it, likes: newLikes }; } } catch (e) {}
      return it;
    }));
    updateArr(setToday);
    updateArr(setUpcoming);
    setCategoriesToday(prev => (prev || []).map(cat => ({ ...cat, events: (cat.events || []).map(ev => (String(ev.id || ev._id || ev.title) === String(eventId) ? { ...ev, likes: newLikes } : ev)) })));
    setCategoriesUpcoming(prev => (prev || []).map(cat => ({ ...cat, events: (cat.events || []).map(ev => (String(ev.id || ev._id || ev.title) === String(eventId) ? { ...ev, likes: newLikes } : ev)) })));
  };

  const handleToggleLike = async (ev, e) => {
    try {
      if (e && e.stopPropagation) e.stopPropagation();
      const id = ev && (ev.id || ev._id || ev.title);
      if (!id) return;
      // Optimistic UI flip: increment locally while server call completes
      const current = ev.likes || 0;
      updateEventLikesInState(id, current + 1);
      const serverLikes = await likeEvent(id);
      if (typeof serverLikes === 'number') updateEventLikesInState(id, serverLikes);
    } catch (err) {
      // best-effort: ignore errors and don't revert optimistic update
    }
  };

  const handleEventClick = async (ev) => {
    try {
      const id = ev && (ev.id || ev._id || ev.title);
      if (id) {
        // fire-and-forget tracking
        trackClick(id);
      }
    } catch (e) {}
    setSelectedEvent(ev);
  };

  // IntersectionObserver to detect when event cards scroll into view -> count as view
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return; // older browsers
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const node = entry.target;
        const id = node && node.getAttribute && node.getAttribute('data-zz-event-id');
        if (!id) return;
        if (seenViewsRef.current.has(id)) return;
        seenViewsRef.current.add(id);
        trackView(id);
      });
    }, { root: null, rootMargin: '0px', threshold: 0.5 });

    // Observe matching nodes
    const selector = '.pl-card.detail[data-zz-event-id]';
    const nodes = Array.from(document.querySelectorAll(selector));
    nodes.forEach(n => obs.observe(n));

    // Re-observe when DOM changes (basic mutation observer to catch list updates)
    const mo = new MutationObserver(() => {
      try {
        const current = Array.from(document.querySelectorAll(selector));
        current.forEach(n => {
          if (!(n._zzObserved)) { obs.observe(n); n._zzObserved = true; }
        });
      } catch (e) {}
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => { try { obs.disconnect(); mo.disconnect(); } catch (e) {} };
  }, []);

  // open event from chat assistant
  useEffect(() => {
    const handler = (e) => {
      try {
        const ev = e?.detail?.event;
        if (!ev) return;
        setSelectedEvent(ev);
        setTimeout(() => {
          try {
            const id = ev.id || ev.title;
            if (!id) return;
            const safe = String(id).replace(/"/g, '\\"');
            const selector = `[data-zz-event-id="${safe}"]`;
            const node = document.querySelector(selector);
            if (node) {
              node.scrollIntoView({ behavior: 'smooth', block: 'center' });
              node.classList.add('zz-highlight');
              setTimeout(() => node.classList.remove('zz-highlight'), 2200);
            }
          } catch (err) {
            // highlight failed
          }
        }, 220);
      } catch (err) {
        // ignore
      }
    };
    window.addEventListener('zz:openEvent', handler);
    return () => window.removeEventListener('zz:openEvent', handler);
  }, []);

  // Places fetching
  const fetchPlaces = async (category = 'restaurants', radius = 1200) => {
    setPlacesError(null); setPlacesLoading(true); setPlacesCategory(category);
    try {
      const lat = (location && (location.latitude || location.lat));
      const lon = (location && (location.longitude || location.lng || location.lon));
      if (!lat || !lon) { setPlacesError('No coordinates available'); setPlaces([]); return; }

      // Try to read from local cache first and show instantly
      const cacheKey = cacheKeyFor(category, lat, lon, radius);
      const cached = readCache(cacheKey);
      if (cached && cached.length) {
        setPlaces(cached);
        // continue to fetch in background to refresh cache
        (async () => {
          try {
            const url2 = `${CRM_API_BASE.replace(/\/+$/,'')}/places?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&category=${encodeURIComponent(category)}&radius=${encodeURIComponent(radius)}&limit=12&excludeUnnamed=1`;
            const res2 = await fetchJson(url2, { credentials: 'include', headers: { Accept: 'application/json' } }, 12000);
            if (!res2.ok) return;
            const j2 = await res2.json(); const list2 = Array.isArray(j2?.data) ? j2.data : (Array.isArray(j2) ? j2 : []);
            if (list2 && list2.length) {
              writeCache(cacheKey, list2);
              setPlaces(list2);
            }
          } catch (e) { /* background refresh failed */ }
        })();
        setPlacesLoading(false);
        return;
      }

  const url = `${CRM_API_BASE.replace(/\/+$/,'')}/places?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&category=${encodeURIComponent(category)}&radius=${encodeURIComponent(radius)}&limit=12&excludeUnnamed=1`;
  const res = await fetchJson(url, { credentials: 'include', headers: { Accept: 'application/json' } }, 12000);
      if (!res.ok) throw new Error(`places ${res.status}`);
      const j = await res.json(); const list = Array.isArray(j?.data) ? j.data : (Array.isArray(j) ? j : []);
      setPlaces(list);
      if (list && list.length) writeCache(cacheKey, list);
    } catch (e) { console.warn('fetchPlaces failed', e && e.message); setPlacesError('Unable to load places'); setPlaces([]); } finally { setPlacesLoading(false); }
  };

  const requestBrowserLocation = async () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) { setError('Geolocation not available'); setShowLocationPrompt(false); try{ localStorage.setItem('zz_location_prompt_dismissed','1'); }catch(e){}; return; }
    setLoading(true);
    try { const pos = await new Promise((res,rej)=>navigator.geolocation.getCurrentPosition(res,rej,{ timeout:20000 })); const lat = pos.coords.latitude; const lon = pos.coords.longitude; let city = null; try{ city = await safeFetchGeo(lat,lon); }catch(e){} const obj = { latitude: lat, longitude: lon }; if(city) obj.city = city; try{ localStorage.setItem('zz_location', JSON.stringify(obj)); }catch(e){} setLocation(obj); setShowLocationPrompt(false); } catch(e){ setError('Unable to get location'); } finally { setLoading(false); }
  };

  const dismissLocationPrompt = () => { try{ localStorage.setItem('zz_location_prompt_dismissed','1'); }catch(e){}; setShowLocationPrompt(false); };

  const getEventImage = (ev) => ev && (ev.image || ev.imageUrl || ev.coverImage || ev.photo || ev.image_url || ev.imageLink) || '';
  const shortDesc = (s, n = 140) => { if (!s) return ''; const t = String(s).replace(/\s+/g,' ').trim(); return t.length > n ? t.slice(0,n).trim() + '…' : t; };

  // Helper: filter out unnamed or placeholder places (e.g., 'Unknown')
  const isNamedPlace = (p) => {
    if (!p) return false;
    const t = (p.title || '').toString().trim();
    if (!t) return false;
    if (/^unknown$/i.test(t)) return false;
    return true;
  };

  const namedPlaces = (places && places.length) ? places.filter(isNamedPlace) : [];

  // Helper: open Google Maps directions to a place. Uses user's location as origin when available.
  const openDirections = (p) => {
    try {
      if (!p) return;
      // destination lat/lon fallback keys
      const destLat = p.lat || p.latitude || p.location?.lat || p.y || p.lat_deg;
      const destLon = p.lon || p.longitude || p.location?.lon || p.x || p.lon_deg;
      let destination = '';
      if (destLat && destLon) {
        destination = `${destLat},${destLon}`;
      } else {
        const q = p.address || p.title || p.name || '';
        destination = q;
      }

      const originLat = location && (location.latitude || location.lat);
      const originLon = location && (location.longitude || location.lon || location.lng);

      let url = 'https://www.google.com/maps/dir/?api=1';
      if (originLat && originLon) url += `&origin=${encodeURIComponent(originLat + ',' + originLon)}`;
      url += `&destination=${encodeURIComponent(destination)}`;
      // Prefer driving; feel free to change to walking if you prefer
      url += '&travelmode=driving';

      // Open in a new tab/window
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    // try to load cached news summary first; otherwise fetch (attempt geo fallback if no stored location)
    let cancelled = false;
    const loadNews = async () => {
      try {
        const cached = localStorage.getItem('zz_news_summary');
        if (cached) { setNewsSummary(cached); return; }
      } catch (e) { /* ignore localStorage errors */ }

      setNewsLoading(true); setNewsError(null);
      try {
        let lat = (location && (location.latitude || location.lat));
        let lon = (location && (location.longitude || location.lon || location.lng));

        // If we don't have coords yet, try a one-time browser geolocation request
        if ((!lat || !lon) && typeof navigator !== 'undefined' && navigator.geolocation) {
          try {
            const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 10000 }));
            lat = lat || pos.coords.latitude;
            lon = lon || pos.coords.longitude;
          } catch (e) {
            // ignore geolocation failure — backend requires coords, so we'll surface a friendly message below
          }
        }

        if (!lat || !lon) {
          // backend requires lat+lon for /api/news; show friendly message
          setNewsError('Location required to load local news');
          return;
        }

        const params = [`lat=${encodeURIComponent(lat)}`, `lon=${encodeURIComponent(lon)}`];
  const url = `${CRM_API_BASE.replace(/\/+$/,'')}/news?${params.join('&')}`;
  const res = await fetchJson(url, { credentials: 'include', headers: { Accept: 'application/json' } }, 12000);
        if (!res.ok) throw new Error(`news ${res.status}`);
        const j = await res.json();
        const text = (j && (j.summary || j.data || j)) ? (j.summary || j.data || (typeof j === 'string' ? j : JSON.stringify(j))) : null;
        if (!cancelled) {
          if (text) {
            const asString = typeof text === 'string' ? text : JSON.stringify(text);
            setNewsSummary(asString);
            try { localStorage.setItem('zz_news_summary', asString); } catch (e) { /* ignore */ }
          } else {
            setNewsError('No news available for your area yet.');
          }
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('loadNews failed', e && e.message);
          setNewsError('Unable to load news');
        }
      } finally { if (!cancelled) setNewsLoading(false); }
    };

    loadNews();
    return () => { cancelled = true; };
  }, [location]);

  // NOTE: using a fixed set of categories avoids UI flapping while Overpass discovery runs.

  // show exactly what backend returns; do not inject frontend-only defaults

  // When a places category is selected, scroll the detail view into center and highlight it briefly
  useEffect(() => {
    if (!selectedPlacesCategory) return;
    // wait for DOM to render
    const id = setTimeout(() => {
      try {
        const node = placesDetailRef.current;
        if (node && typeof node.scrollIntoView === 'function') {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' });
          node.classList.add('zz-highlight');
          setTimeout(() => node.classList.remove('zz-highlight'), 2200);
        }
      } catch (e) {
        // ignore
      }
    }, 140);
    return () => clearTimeout(id);
  }, [selectedPlacesCategory]);

  // compute bottom-nav active state based on current path so the correct tab is highlighted
  const currentPath = (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : '';
  const isHomeActive = currentPath === '/' || currentPath === '/webapp' || currentPath.includes('/webapp/home');
  const isMapActive = currentPath.includes('/webapp/map');
  const isLocalsActive = false; // locals is a scroll action within the home page

  return (
    <div className="postlogin-root">
      {viewMode === 'reels' ? (
        <VideoReels />
      ) : (
        <>
          <header className="pl-header">
            <div>
              <h1>Welcome{location ? ` to ${location.city || location.name || ''}` : ''}</h1>
              <p className="pl-sub">Connect locally.</p>
            </div>
            <div className="pl-cta">
              <a className="zz-btn primary" href="https://www.zonezapp.com/r/chat/%7B%22longitude%22%3A-66.64742097258568%2C%22latitude%22%3A45.96293436953767%7D" target="_blank" rel="noopener noreferrer">Connect with locals</a>
              <a className="zz-btn" href="/webapp/visitor-center" style={{ background: 'rgba(255, 255, 255, 0.2)', color: 'white', border: '2px solid rgba(255, 255, 255, 0.3)', backdropFilter: 'blur(10px)' }}>🎯 Visitor Info</a>
            </div>
          </header>

          {showLocationPrompt && !location && (
        <div className="pl-location-prompt">
          <div className="pl-location-body">
            <strong>Enable location to see local events</strong>
            <p className="small">We use your approximate location to find events near you. You can dismiss this and try later.</p>
          </div>
          <div className="pl-location-actions">
            <button className="zz-btn primary" onClick={requestBrowserLocation} disabled={loading}>Enable location</button>
            <button className="zz-btn flat" onClick={dismissLocationPrompt} disabled={loading} style={{ marginLeft: 8 }}>Maybe later</button>
          </div>
        </div>
      )}

      <main className="pl-main">
        <section className="pl-col pl-events">
          <h2>What's to do today in {location ? (location.city || location.name) : 'your area'}</h2>

          {loading && <div className="pl-loading">Loading events…</div>}
          {error && <div className="pl-error">{error}</div>}

          {!selectedCategory ? (
            <>
              {categoriesToday && categoriesToday.length > 0 ? (
                <div className="pl-categories-card">
                  <h4 style={{ margin: '6px 0' }}>What's to do today</h4>
                  <div className="pl-categories">
                    {categoriesToday.map(cat => (
                      <button key={cat.category} className="pl-category-chip" onClick={() => setSelectedCategory({ ...cat, source: 'today' })}>{cat.category} ({cat.count})</button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="pl-empty">No categories found for today.</div>
              )}

              {categoriesUpcoming && categoriesUpcoming.length > 0 ? (
                <div className="pl-categories-card" style={{ marginTop: 16 }}>
                  <h4 style={{ margin: '6px 0' }}>What's happening soon</h4>
                  <div className="pl-categories">
                    {categoriesUpcoming.map(cat => (
                      <button key={cat.category} className="pl-category-chip" onClick={() => setSelectedCategory({ ...cat, source: 'upcoming' })}>{cat.category} ({cat.count})</button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="pl-empty">No upcoming categories found.</div>
              )}
            </>
          ) : (
            <div className="pl-category-detail">
              <div className="pl-category-detail-header">
                <button className="zz-btn flat pl-back-btn" onClick={() => setSelectedCategory(null)} aria-label="Back to categories">Back</button>
                <h3 style={{ display: 'inline-block' }}>{selectedCategory.category} ({selectedCategory.count})</h3>
              </div>
              <div className="pl-category-events" style={{ maxHeight: '60vh', overflowY: 'auto', marginTop: 12 }}>
                {selectedCategory.events && selectedCategory.events.length > 0 ? selectedCategory.events.map(ev => (
                  <article key={ev.id || ev.title} data-zz-event-id={ev.id || ev.title} className="pl-card detail" onClick={() => handleEventClick(ev)} style={{ cursor: 'pointer' }}>
                    <div className="pl-card-image" style={{ width: '100%', height: 180, overflow: 'hidden', borderRadius: 10, background: '#eee' }}>
                      {getEventImage(ev) ? (
                        <img src={getEventImage(ev)} alt={ev.title || 'event image'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : null}
                    </div>
                    <div className="pl-card-body">
                      <h3 style={{ marginTop: 10 }}>{ev.title || ev.name}</h3>
                      <div className="pl-meta">{ev.venueAddress || ev.venue || ev.location || ''} • {FriendlyDate(ev.start || ev.date || ev.timestamp)}</div>
                      {ev.description && <p className="pl-desc">{shortDesc(ev.description)}</p>}
                      <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
                        <button className="zz-btn flat" onClick={(e) => handleToggleLike(ev, e)} aria-label="Like this event">❤️ {ev.likes || 0}</button>
                        <div style={{ color: '#666', fontSize: 13 }}>👁️ {ev.views || 0} • 🔗 {ev.clicks || 0}</div>
                      </div>
                    </div>
                  </article>
                )) : (
                  <div className="pl-empty">No events in this category.</div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Places as its own card/column */}
        <section className="pl-col pl-places">
          <div className="pl-news-card">
            <h3 style={{ margin: 0 }}>Places</h3>
            <div style={{ marginTop: 8 }}>
              <div className="pl-categories">
                {FIXED_PLACE_CATEGORIES.map(c => ({ id: c, label: labelize(c) })).map(cat => (
                  <button
                    key={cat.id}
                    className="pl-category-chip"
                    onClick={() => { setSelectedPlacesCategory({ category: cat.id, label: cat.label, count: cat.count }); setPlacesCategory(cat.id); fetchPlaces(cat.id, placesRadius); }}
                    style={{ background: placesCategory === cat.id ? 'linear-gradient(90deg,#fe8b48,#ff6b6b)' : undefined, color: placesCategory === cat.id ? '#fff' : undefined, marginRight: 8 }}
                  >{cat.label}{cat.count ? ` (${cat.count})` : ''}</button>
                ))}
                <div style={{ display: 'inline-block', marginLeft: 8, verticalAlign: 'middle' }}>
                  <button className="zz-btn flat" onClick={() => {
                    const newR = Math.max(200, placesRadius - 500);
                    setPlacesRadius(newR);
                    try { localStorage.setItem('zz_places_radius', String(newR)); } catch (e) {}
                    if (placesCategory) fetchPlaces(placesCategory, newR);
                  }}>-</button>
                  <span style={{ margin: '0 8px', fontSize: 13 }}>{placesRadius}m</span>
                  <button className="zz-btn flat" onClick={() => {
                    const newR = placesRadius + 500;
                    setPlacesRadius(newR);
                    try { localStorage.setItem('zz_places_radius', String(newR)); } catch (e) {}
                    if (placesCategory) fetchPlaces(placesCategory, newR);
                  }}>+</button>
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                {!selectedPlacesCategory ? (
                  // Compact view: show a preview or message
                  placesLoading ? (
                    <div className="pl-loading">Loading places…</div>
                  ) : placesError ? (
                    <div className="pl-empty">{placesError}</div>
                  ) : namedPlaces && namedPlaces.length > 0 ? (
                    <div className="pl-list">
                            {namedPlaces.slice(0,3).map(p => (
                              <article key={p.providerId || p.id || p.title} data-zz-place-id={p.providerId || p.id || p.title} className="pl-card detail" onClick={() => { try { window.dispatchEvent(new CustomEvent('zz:openPlace', { detail: { place: p } })); openDirections(p); } catch (e) {} }} style={{ cursor: 'pointer' }}>
                                {p.image ? (
                                  <div className="pl-card-image" style={{ width: '100%', height: 140, overflow: 'hidden', borderRadius: 8, background: '#eee' }}>
                                    <img src={p.image} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  </div>
                                ) : null}
                          <div className="pl-card-body">
                            <h3 style={{ marginTop: 10 }}>{p.title}</h3>
                            <div className="pl-meta">{p.address || ''} {p.distanceMeters ? `• ${Math.round(p.distanceMeters)}m` : ''}</div>
                            {p.description && <p className="pl-desc">{shortDesc(p.description, 140)}</p>}
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    // When no category is selected yet and there are no preview places, don't show a 'No places' message.
                    // The user can pick a category chip to load places.
                    null
                  )
                ) : (
                  // Detail view for selected category (same UX as events)
                  <div className="pl-category-detail" ref={placesDetailRef}>
                    <div className="pl-category-detail-header">
                      <button className="zz-btn flat pl-back-btn" onClick={() => { setIsPlacesCollapsing(true); setTimeout(() => { setSelectedPlacesCategory(null); setPlaces([]); setIsPlacesCollapsing(false); }, 220); }} aria-label="Back to categories">Back</button>
                      <h3 style={{ display: 'inline-block' }}>{selectedPlacesCategory.label ? selectedPlacesCategory.label : (selectedPlacesCategory.category ? (selectedPlacesCategory.category.charAt(0).toUpperCase() + selectedPlacesCategory.category.slice(1)) : '')}</h3>
                      <div style={{ marginLeft: 12, display: 'inline-block', verticalAlign: 'middle' }}>
                        <button className="zz-btn flat" onClick={() => {
                          const newR = Math.max(200, placesRadius - 500);
                          setPlacesRadius(newR);
                          fetchPlaces(selectedPlacesCategory.category, newR);
                        }}>-</button>
                        <span style={{ margin: '0 8px', fontSize: 13 }}>{placesRadius}m</span>
                        <button className="zz-btn flat" onClick={() => {
                          const newR = placesRadius + 500;
                          setPlacesRadius(newR);
                          fetchPlaces(selectedPlacesCategory.category, newR);
                        }}>+</button>
                      </div>
                    </div>
                    <div className={`pl-category-events ${isPlacesCollapsing ? 'zz-exit' : 'zz-enter'}`} style={{ maxHeight: '60vh', overflowY: 'auto', marginTop: 12 }}>
                      {placesLoading ? (
                        <div className="pl-loading">Loading places…</div>
                      ) : placesError ? (
                        <div className="pl-empty">{placesError}</div>
                      ) : namedPlaces && namedPlaces.length > 0 ? (
                        namedPlaces.map(p => (
                          <article key={p.providerId || p.id || p.title} data-zz-place-id={p.providerId || p.id || p.title} className="pl-card detail" onClick={() => { try { window.dispatchEvent(new CustomEvent('zz:openPlace', { detail: { place: p } })); openDirections(p); } catch (e) {} }} style={{ cursor: 'pointer' }}>
                            {p.image ? (
                              <div className="pl-card-image" style={{ width: '100%', height: 140, overflow: 'hidden', borderRadius: 8, background: '#eee' }}>
                                <img src={p.image} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              </div>
                            ) : null}
                            <div className="pl-card-body">
                              <h3 style={{ marginTop: 10 }}>{p.title}</h3>
                              <div className="pl-meta">{p.address || ''} {p.distanceMeters ? `• ${Math.round(p.distanceMeters)}m` : ''}</div>
                              {p.description && <p className="pl-desc">{shortDesc(p.description, 140)}</p>}
                            </div>
                          </article>
                        ))
                      ) : (
                        <div className="pl-empty">No places found for this category.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <aside className="pl-col pl-chat">
          <div className="pl-chat-header">
            <h3>Ask guidance</h3>
            <p className="small">Ask the assistant about local events, places, or how to connect with neighbors.</p>
          </div>

          <div className="pl-news-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0 }}>Local news</h4>
              <div>
                <button className="zz-btn flat" onClick={() => { try { localStorage.removeItem('zz_news_summary'); setNewsSummary(null); setNewsError(null); setNewsExpanded(false); } catch (e) { /* ignore */ } }}>Refresh</button>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              {newsLoading ? <div className="pl-loading">Loading news…</div> : newsError ? <div className="pl-empty">{newsError}</div> : newsSummary ? (
                <>
                  <div className="pl-news-summary" style={{ maxHeight: newsExpanded ? 'none' : 120, overflow: 'hidden', whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: newsSummary }} />
                  <div style={{ marginTop: 8 }}>
                    <button className="zz-btn flat" onClick={() => setNewsExpanded(prev => !prev)}>{newsExpanded ? 'Show less' : 'Read more'}</button>
                  </div>
                </>
              ) : <div className="pl-empty">No news available for your area yet.</div>}
            </div>
          </div>
        </aside>
      </main>

      {/* Chat Assistant - Moved outside to be globally visible */}
      <ChatAssistant />

      <style>{`.zz-highlight{ box-shadow: 0 0 0 4px rgba(255,150,0,0.18); transform: translateY(-2px); transition: box-shadow .3s, transform .3s; }`}</style>
      <style>{`
        .zz-enter{ animation: zzFadeIn .22s ease forwards; }
        .zz-exit{ animation: zzFadeOut .18s ease forwards; }
        @keyframes zzFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes zzFadeOut { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(6px); } }
      `}</style>

      {selectedEvent && (
        <div className="pl-event-modal" role="dialog" aria-modal="true" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div className="pl-event-panel" style={{ width: '92%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <button className="zz-btn flat" onClick={() => setSelectedEvent(null)} style={{ marginRight: 12 }}>Close</button>
              <h2 style={{ margin: 0 }}>{selectedEvent.title || selectedEvent.name}</h2>
            </div>
            {getEventImage(selectedEvent) ? (
              <div style={{ width: '100%', height: 320, overflow: 'hidden', borderRadius: 8 }}>
                <img src={getEventImage(selectedEvent)} alt={selectedEvent.title || 'event image'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ) : null}
            <div style={{ marginTop: 12 }}>
              <div className="pl-meta" style={{ color: '#666', marginBottom: 8 }}>{selectedEvent.venueAddress || selectedEvent.venue || selectedEvent.location || ''} • {FriendlyDate(selectedEvent.start || selectedEvent.date || selectedEvent.timestamp)}</div>
              {selectedEvent.description && <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{selectedEvent.description}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Bottom menu (fixed) */}
      <nav style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 12, zIndex: 9999, width: 480, maxWidth: '95%', display: 'flex', justifyContent: 'center' }} aria-label="Primary">
        <div style={{ background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 252, 0.98) 100%)', borderRadius: 28, boxShadow: '0 8px 32px rgba(102, 126, 234, 0.2), 0 2px 8px rgba(0,0,0,0.08)', display: 'flex', padding: 8, width: '100%', alignItems: 'center', justifyContent: 'space-around', backdropFilter: 'blur(10px)', border: '2px solid rgba(102, 126, 234, 0.1)' }}>
          <button onClick={() => { setViewMode('feed'); }} style={{ background: (isHomeActive && viewMode === 'feed') ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent', border: 'none', color: (isHomeActive && viewMode === 'feed') ? '#fff' : '#64748b', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', borderRadius: 16, minWidth: 70, transition: 'all 0.3s ease', fontWeight: 600, fontSize: 13 }} aria-label="Home">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 11.5L12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-8.5z" fill="currentColor"/></svg>
            <div style={{ marginTop: 4 }}>Home</div>
          </button>
          
          <button onClick={() => { setViewMode('reels'); }} style={{ background: viewMode === 'reels' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent', border: 'none', color: viewMode === 'reels' ? '#fff' : '#64748b', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', borderRadius: 16, minWidth: 70, transition: 'all 0.3s ease', fontWeight: 600, fontSize: 13 }} aria-label="Reels">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" fill="currentColor"/></svg>
            <div style={{ marginTop: 4 }}>Reels</div>
          </button>

          <button onClick={() => { window.location.href = '/webapp/map'; }} style={{ background: isMapActive ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent', border: 'none', color: isMapActive ? '#fff' : '#64748b', borderRadius: 16, padding: '12px 20px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.3s ease', fontSize: 14 }} aria-label="Map">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="currentColor"/></svg>
            Map
          </button>

          <button onClick={() => {
              // scroll to Places section if visible
              try { const el = document.querySelector('.pl-places'); if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('zz-highlight'); setTimeout(()=>el.classList.remove('zz-highlight'), 2000); } else { window.location.href = '/webapp/home'; }
              } catch (e) { window.location.href = '/webapp/home'; }
            }} style={{ background: isLocalsActive ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent', border: 'none', color: isLocalsActive ? '#fff' : '#64748b', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', borderRadius: 16, minWidth: 70, transition: 'all 0.3s ease', fontWeight: 600, fontSize: 13 }} aria-label="Locals">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-5 0-9 2.5-9 5v1h18v-1c0-2.5-4-5-9-5z" fill="currentColor"/></svg>
            <div style={{ marginTop: 4 }}>Locals</div>
          </button>
        </div>
      </nav>
      </>
      )}
    </div>
  );
}
